// The public booking page and the admin dashboard share this catalogue shape.
// Once an administrator saves changes, the live version is stored at
// settings/bookingServices. These defaults keep the booking page available on
// first deploy and let the dashboard initialise the catalogue without a manual
// database migration.

export const BOOKING_SERVICES_SETTINGS_ID = "bookingServices";
export const BOOKING_CONTACT_EMAIL = "bookings@jabuzwane.co.za";

export const DEFAULT_BOOKING_SERVICES = [
	{
		id: "relationship-counselling",
		title: "Marriage/Couples Counselling",
		tagline: "For couples working through conflict, trust, or connection.",
		duration: 60,
		active: true,
		allowOnlinePayment: true,
		modes: ["in-person", "virtual"],
		packages: [{ id: "standard", label: "60-minute session", priceType: "fixed", priceCents: 85000 }],
	},
	{
		id: "individual-counselling",
		title: "Individual Counselling",
		tagline: "One-on-one support for personal growth and wellbeing.",
		duration: 60,
		active: true,
		allowOnlinePayment: true,
		modes: ["in-person", "virtual"],
		packages: [{ id: "standard", label: "60-minute session", priceType: "fixed", priceCents: 97500 }],
	},
	{
		id: "coaching",
		title: "Coaching",
		tagline: "Goal-focused sessions to help you move forward with clarity.",
		duration: 60,
		active: true,
		allowOnlinePayment: true,
		modes: ["in-person", "virtual"],
		packages: [{ id: "standard", label: "60-minute session", priceType: "fixed", priceCents: 70000 }],
	},
	{
		id: "keynote-speaker",
		title: "Keynote Speaker",
		tagline: "Book a talk or workshop for your event or organisation.",
		duration: 60,
		active: true,
		allowOnlinePayment: false,
		modes: ["in-person", "virtual"],
		packages: [{ id: "custom", label: "Enquire about a talk or workshop", priceType: "on-request", priceCents: null }],
	},
	{
		id: "premarital-counselling",
		title: "Premarital Counselling",
		tagline: "Structured support to prepare thoughtfully for marriage.",
		duration: 60,
		active: true,
		// The requested service is intentionally booked as an enquiry /
		// pay-at-appointment service, even for the listed packages.
		allowOnlinePayment: false,
		modes: ["in-person", "virtual"],
		packages: [
			{ id: "one-session", label: "1 Session", priceType: "fixed", priceCents: 147500 },
			{ id: "three-sessions", label: "3 Sessions", priceType: "fixed", priceCents: 397500 },
			{ id: "seven-sessions", label: "7 Sessions", priceType: "fixed", priceCents: 787500 },
			{ id: "custom", label: "Enquire for a customised option", priceType: "on-request", priceCents: null },
		],
	},
];

export function formatZarCents(cents) {
	const value = Number(cents);
	if (!Number.isFinite(value) || value < 0) return "Price on request";
	return `R ${new Intl.NumberFormat("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value / 100)}`;
}

export function parseZarCents(value) {
	const text = String(value ?? "").trim();
	const match = text.match(/(?:R\s*)?([\d,]+(?:\.\d+)?)/i);
	const amount = match ? Number(match[1].replace(/,/g, "")) : NaN;
	return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : null;
}

function clone(value) {
	return JSON.parse(JSON.stringify(value));
}

// Retain any prices already saved through the old ServicePricing panel until
// the administrator first saves the new catalogue.
export function defaultBookingServices(legacyPrices = {}) {
	return clone(DEFAULT_BOOKING_SERVICES).map((service) => {
		const legacyCents = parseZarCents(legacyPrices[service.id]);
		if (legacyCents === null || service.packages.length !== 1 || service.packages[0].priceType !== "fixed") {
			return service;
		}
		return {
			...service,
			packages: [{ ...service.packages[0], priceCents: legacyCents }],
		};
	});
}

export function normaliseBookingServices(value, fallback = DEFAULT_BOOKING_SERVICES) {
	// A missing catalogue means this is a first-time setup, so use the built-in
	// services. An explicitly empty array is different: it is the admin's choice
	// to pause all bookings and must not silently restore old services.
	if (!Array.isArray(value)) return clone(fallback);

	const services = value
		.map((service, serviceIndex) => {
			if (!service || typeof service !== "object" || !String(service.title || "").trim()) return null;
			const packages = Array.isArray(service.packages) && service.packages.length
				? service.packages
				: [{ id: "standard", label: "Session", priceType: "on-request", priceCents: null }];
			return {
				id: String(service.id || `service-${serviceIndex + 1}`),
				title: String(service.title).trim(),
				tagline: String(service.tagline || "").trim(),
				duration: Math.max(15, Math.min(480, Number(service.duration) || 60)),
				active: service.active !== false,
				allowOnlinePayment: service.allowOnlinePayment === true,
				modes: Array.isArray(service.modes) && service.modes.length
					? service.modes.filter((mode) => mode === "in-person" || mode === "virtual")
					: ["in-person", "virtual"],
				packages: packages.map((pkg, packageIndex) => ({
					id: String(pkg?.id || `option-${packageIndex + 1}`),
					label: String(pkg?.label || `Option ${packageIndex + 1}`).trim(),
					priceType: pkg?.priceType === "on-request" ? "on-request" : "fixed",
					priceCents: pkg?.priceType === "on-request"
						? null
						: Math.max(0, Math.round(Number(pkg?.priceCents) || 0)),
				})),
			};
		})
		.filter(Boolean);

	return services;
}

export function packagePriceLabel(pkg) {
	return pkg?.priceType === "on-request" ? "Price on request" : formatZarCents(pkg?.priceCents);
}

export function servicePriceLabel(service) {
	const packages = service?.packages || [];
	const fixedPackages = packages.filter((pkg) => pkg.priceType === "fixed");
	if (!fixedPackages.length) return "Price on request";
	const lowest = Math.min(...fixedPackages.map((pkg) => Number(pkg.priceCents) || 0));
	return packages.length > 1 ? `From ${formatZarCents(lowest)}` : formatZarCents(lowest);
}

export function serviceOptionForBooking(service, pkg) {
	const quoteOnly = pkg.priceType === "on-request";
	return {
		id: service.id,
		serviceId: service.id,
		title: service.title,
		bookingTitle: service.packages.length > 1 ? `${service.title} — ${pkg.label}` : service.title,
		packageId: pkg.id,
		packageLabel: pkg.label,
		tagline: service.tagline,
		duration: service.duration,
		price: packagePriceLabel(pkg),
		priceCents: quoteOnly ? null : pkg.priceCents,
		quoteOnly,
		allowOnlinePayment: service.allowOnlinePayment === true && !quoteOnly,
		modes: service.modes,
	};
}
